import {useContext, useEffect, useState} from 'react'
import Drawer from '@mui/material/Drawer'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import TextField from '@mui/material/TextField'
import InputAdornment from '@mui/material/InputAdornment'
import CloseIcon from '@mui/icons-material/Close'
import SearchIcon from '@mui/icons-material/Search'
import Chip from '@mui/material/Chip'
import TablePagination from '@mui/material/TablePagination'
import LinearProgress from '@mui/material/LinearProgress'
import CircularProgress from '@mui/material/CircularProgress'
import {SpaceContext} from '../SpaceProvider.tsx'
import {taruviClient} from '../taruviClient.ts'

// Shape returned by api/widgets/gallery/
interface GalleryWidget {
  slug: string
  app_slug: string
  app_name: string
  name: string
  description?: string
  preview_url?: string | null
}

const PAGE_SIZE_OPTIONS = [10, 20, 50] as const

const widgetKey = (w: {app_slug: string, slug: string}) => `${w.app_slug}:${w.slug}`

const AddWidgetDrawer = ({open, onClose}: { open: boolean, onClose: () => void }) => {
  const [, spaceConfig, setSpaceConfig] = useContext(SpaceContext)! // widgets fetched locally, not from context
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [selected, setSelected] = useState<string | null>(null)
  const [page, setPage] = useState(0) // MUI TablePagination is 0-based; API page is 1-based
  const [rowsPerPage, setRowsPerPage] = useState(20)
  const [widgets, setWidgets] = useState<GalleryWidget[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)

  // Debounce search so we don't hit the API on every keystroke
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300)
    return () => clearTimeout(timer)
  }, [search])

  // Fetch paginated gallery when drawer opens or page/search changes
  useEffect(() => {
    if (!open) return

    let cancelled = false // ignore stale responses when deps change quickly
    setLoading(true)

    const params = new URLSearchParams({
      page: String(page + 1),
      page_size: String(rowsPerPage),
    })
    if (debouncedSearch.trim()) params.set('name', debouncedSearch.trim())

    taruviClient.httpClient
      .get<{data: GalleryWidget[]; total: number}>(`api/widgets/gallery/?${params}`)
      .then((res) => {
        if (!cancelled) {
          setWidgets(res?.data ?? [])
          setTotal(res?.total ?? 0)
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => { cancelled = true }
  }, [open, page, rowsPerPage, debouncedSearch])

  const handleSearchChange = (value: string) => {
    setSearch(value)
    setPage(0) // reset to first page on new search
  }

  // A widget counts as added if a non-removed instance of it exists in the space
  const isWidgetAdded = (widget: GalleryWidget) =>
    spaceConfig.some((w: any) => !w.is_removed && w.slug === widget.slug && w.appSlug === widget.app_slug)

  const handleAdd = async (widget: GalleryWidget) => {
    setSelected(widgetKey(widget))
    const widgetData: any = await taruviClient.httpClient.get(`api/apps/${widget.app_slug}/widgets/${widget.slug}/`)
    const id = `${widget.slug}-${Date.now()}`
    setSpaceConfig((prev: any) => {
      // Guard against duplicates (e.g. rapid double clicks before state updates)
      if (prev.some((w: any) => !w.is_removed && w.slug === widget.slug && w.appSlug === widget.app_slug)) return prev
      return [...prev, {
        id, slug: widget.slug,
        appSlug: widget.app_slug,
        react_code_content: widgetData?.data?.react_code_content,
        style_code_content: widgetData?.data?.style_code_content,
        metadata: widgetData?.data?.metadata,
        layouts: {i: id, x: 0, y: Infinity, w: widgetData?.data?.metadata?.width || 2, h: widgetData?.data?.metadata?.height || 2},
      }]
    })
    setTimeout(() => setSelected(null), 300)
  }

  return (
    // Column layout pins pagination to the bottom of the drawer
    <Drawer open={open} onClose={onClose} anchor="right"
            PaperProps={{sx: {width: 360, display: 'flex', flexDirection: 'column'}}}>

      <Box sx={{p: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
        <Typography variant="h6" fontWeight={700}>Widget Library</Typography>
        <IconButton onClick={onClose} size="small"><CloseIcon/></IconButton>
      </Box>

      <Box sx={{px: 2.5, mb: 2}}>
        <TextField fullWidth size="small" placeholder="Search widgets..."
                   value={search} onChange={e => handleSearchChange(e.target.value)}
                   slotProps={{input: {startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" color="action"/></InputAdornment>}}}
                   sx={{'& .MuiOutlinedInput-root': {borderRadius: 2}}}/>
      </Box>

      {/* Thin progress bar while fetching (including page changes) */}
      <Box sx={{height: 4, flexShrink: 0}}>{loading && <LinearProgress/>}</Box>

      <Box sx={{px: 2.5, flex: 1, overflow: 'auto'}}>
        {loading && widgets.length === 0 ? (
          <Box sx={{display: 'flex', justifyContent: 'center', py: 6}}>
            <CircularProgress size={28}/>
          </Box>
        ) : widgets.length === 0 ? (
          <Typography fontSize={14} color="text.secondary" sx={{py: 4, textAlign: 'center'}}>
            {debouncedSearch ? 'No widgets match your search' : 'No widgets available'}
          </Typography>
        ) : (
          widgets.map((widget) => {
            const isAdded = isWidgetAdded(widget)
            return (
              <Box key={widgetKey(widget)} onClick={() => { if (!isAdded) handleAdd(widget) }}
                   sx={{
                     mb: 2.5, borderRadius: 3, cursor: isAdded ? 'default' : 'pointer',
                     border: '1px solid',
                     borderColor: selected === widgetKey(widget) ? 'primary.main' : 'divider',
                     '&:hover': {borderColor: isAdded ? 'divider' : 'primary.main'},
                     overflow: 'hidden',
                   }}>
                <Box sx={{bgcolor: '#f8f9fa', p: 2, display: 'flex', justifyContent: 'center'}}>
                  {widget.preview_url ? (
                    <img src={widget.preview_url} alt={widget.name} style={{width: '100%', borderRadius: 8, objectFit: 'cover'}}/>
                  ) : (
                    <Box sx={{height: 120, width: '100%', borderRadius: 2, position: 'relative', overflow: 'hidden', bgcolor: '#eef2f7'}}>
                      <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
                        <defs>
                          <pattern id={`p-${widget.slug}`} width="20" height="20" patternUnits="userSpaceOnUse">
                            <circle cx="2" cy="2" r="1.5" fill="#c5cdd8" opacity="0.5"/>
                          </pattern>
                        </defs>
                        <rect width="100%" height="100%" fill={`url(#p-${widget.slug})`}/>
                        <rect x="15%" y="20%" width="70%" height="18%" rx="4" fill="#dce3ec"/>
                        <rect x="15%" y="46%" width="50%" height="10%" rx="3" fill="#dce3ec"/>
                        <rect x="15%" y="62%" width="60%" height="10%" rx="3" fill="#dce3ec"/>
                        <rect x="15%" y="78%" width="35%" height="10%" rx="3" fill="#dce3ec"/>
                      </svg>
                    </Box>
                  )}
                </Box>
                <Box sx={{p: 2}}>
                  <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1}}>
                    <Typography fontWeight={700} fontSize={16}>{widget.name}</Typography>
                    {isAdded && <Chip label="Added" size="small" sx={{height: 22, fontSize: 11}}/>}
                  </Box>
                  {widget.description && (
                    <Typography fontSize={13} color="text.secondary" sx={{mt: 0.5}}>{widget.description}</Typography>
                  )}
                  {widget.app_name && ( // replaces grouped-by-app sections from pre-pagination UI
                    <Box sx={{mt: 1}}>
                      <Chip label={widget.app_name} size="small"
                            sx={{height: 24, fontSize: 12, bgcolor: '#f0f4ff', color: '#1976d2', fontWeight: 600}}/>
                    </Box>
                  )}
                </Box>
              </Box>
            )
          })
        )}
      </Box>

      {/* Server-side pagination controls */}
      {total > 0 && (
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10))
            setPage(0)
          }}
          rowsPerPageOptions={[...PAGE_SIZE_OPTIONS]}
          labelRowsPerPage="Per page"
          labelDisplayedRows={({from, to, count}) => `${from}–${to} / ${count}`}
          sx={{
            flexShrink: 0,
            borderTop: '1px solid',
            borderColor: 'divider',
            overflow: 'hidden',
            pl: 1.5,
            pr: 2.5,
            '& .MuiTablePagination-toolbar': {minHeight: 40, px: 0, gap: 0.5},
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {fontSize: 11, m: 0},
            '& .MuiTablePagination-select': {fontSize: 11, py: 0.25},
            '& .MuiTablePagination-spacer': {flex: 'none', width: 4},
            '& .MuiTablePagination-actions': {ml: 'auto', pl: 1},
            '& .MuiTablePagination-actions .MuiIconButton-root': {p: 0.5},
          }}
        />
      )}
    </Drawer>
  )
}

export default AddWidgetDrawer
